/**
 * All-pipelines drawer container (SE-5489).
 *
 * Thin orchestration over the shared {@link useSeeAllPipelinesData} hook (which
 * owns the board query, the Personal / Team / System grouping, the updatedAt
 * DESC sort, and the client-side name filter). This Container adds the
 * desktop-only concerns: per-section collapsed state, the search query state
 * (reset on close), and ESC-key dismissal on web.
 *
 * Recency is bumped by the CALLER (the chip row) on select — not here — so the
 * store stays the single writer of recency and the chip row can decide how a
 * fresh pick surfaces (appended active chip). This Container just closes itself
 * and forwards the selected board id.
 * @module features/player-kanban/components/molecules/SeeAllPipelinesDrawer/SeeAllPipelinesDrawerContainer
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";

import SeeAllPipelinesDrawerView from "./SeeAllPipelinesDrawerView";

import type { SelectDropdownOption } from "@/components/molecules/SelectDropdown";
import {
  useSeeAllPipelinesData,
  type PipelineDrawerSort,
} from "@/features/player-kanban/hooks/useSeeAllPipelinesData";

/**
 * Sort menu rows, in display order. `recent` leads because it is the drawer's
 * long-standing default and the question the drawer answers most often is
 * "which pipeline was I just in".
 */
const SORT_OPTIONS: readonly {
  readonly key: PipelineDrawerSort;
  readonly label: string;
}[] = [
  { key: "recent", label: "Recently used" },
  { key: "name", label: "Name (A–Z)" },
];

/** Per-section collapsed state. Default for all sections is expanded (false). */
interface SectionsCollapsed {
  readonly personal: boolean;
  readonly team: boolean;
  readonly system: boolean;
}

/** Props for SeeAllPipelinesDrawerContainer */
export interface SeeAllPipelinesDrawerContainerProps {
  /** Whether the drawer is open. Drives the board query's skip and each row's count-query skip. */
  readonly isOpen: boolean;
  /** Close handler — called on the scrim click and the ESC key. */
  readonly onClose: () => void;
  /**
   * Selection handler — called with the chosen board id AFTER the drawer has
   * closed itself. The receiver activates the pipeline and (for a fresh pick)
   * bumps recency.
   */
  readonly onSelectPipeline: (boardId: string) => void;
  /**
   * Start pipeline creation. The drawer CLOSES first and then calls this, which
   * opens the New Pipeline modal — a pipeline is named and created only there,
   * so this is a route into that modal rather than a second creation path.
   *
   * OPTIONAL, and its absence HIDES the New button: the caller that owns the
   * modal supplies it, and a surface that cannot open the modal should not
   * offer a control that does nothing.
   */
  readonly onCreatePipeline?: () => void;
}

/**
 * Container for the all-pipelines drawer.
 * @param root0 - Component props
 * @param root0.isOpen - Drawer open state
 * @param root0.onClose - Dismissal callback
 * @param root0.onSelectPipeline - Row-tap callback (drawer closes first, then this fires)
 * @param root0.onCreatePipeline - New-pipeline callback (drawer closes first, then this fires)
 */
const SeeAllPipelinesDrawerContainer = ({
  isOpen,
  onClose,
  onSelectPipeline,
  onCreatePipeline,
}: SeeAllPipelinesDrawerContainerProps) => {
  const [collapsed, setCollapsed] = useState<SectionsCollapsed>({
    personal: false,
    team: false,
    system: false,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<PipelineDrawerSort>("recent");
  const [isSortOpen, setIsSortOpen] = useState(false);

  // "Adjust state during render": clear the search query when the drawer closes
  // so the next open starts fresh, without a setState-in-effect. The SORT is
  // deliberately NOT reset — it is a viewing preference, and a user who chose
  // alphabetical order means it for the session, not for one open.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setSearchQuery("");
      setIsSortOpen(false);
    }
  }

  // SE-6775: `hasUnreachableBoards` is the switcher walk reporting that it
  // stopped while the connection still had pages. SE-6597 exposed it and wired
  // it into the chip row; this drawer consumed the same hook and dropped it, so
  // the surface whose entire purpose is "see ALL pipelines" was the last one
  // still presenting a capped list as the complete one.
  const {
    loading,
    error,
    retry,
    personal,
    team,
    system,
    hasUnreachableBoards,
  } = useSeeAllPipelinesData({
    isOpen,
    searchQuery,
    sortOrder,
  });

  const isEmpty =
    !loading &&
    !error &&
    personal.length === 0 &&
    team.length === 0 &&
    system.length === 0;

  const handleTogglePersonal = useCallback(() => {
    setCollapsed(prev => ({ ...prev, personal: !prev.personal }));
  }, []);

  const handleToggleTeam = useCallback(() => {
    setCollapsed(prev => ({ ...prev, team: !prev.team }));
  }, []);

  const handleToggleSystem = useCallback(() => {
    setCollapsed(prev => ({ ...prev, system: !prev.system }));
  }, []);

  const handleSelect = useCallback(
    (boardId: string) => {
      onClose();
      onSelectPipeline(boardId);
    },
    [onClose, onSelectPipeline]
  );

  // Close THEN create — the modal and this drawer are both overlays, and
  // leaving the drawer open behind the modal would stack two scrims and leave
  // the drawer still standing when the modal is dismissed.
  const handleCreate = useCallback(() => {
    if (!onCreatePipeline) return;
    onClose();
    onCreatePipeline();
  }, [onClose, onCreatePipeline]);

  const handleSortToggle = useCallback(() => setIsSortOpen(open => !open), []);
  const handleSortClose = useCallback(() => setIsSortOpen(false), []);

  const sortOptions = useMemo<SelectDropdownOption[]>(
    () =>
      SORT_OPTIONS.map(entry => ({
        id: entry.key,
        label: entry.label,
        selected: sortOrder === entry.key,
        onPress: () => setSortOrder(entry.key),
      })),
    [sortOrder]
  );

  /** The sort trigger's label — the active ordering, spelled out. */
  const sortLabel =
    SORT_OPTIONS.find(entry => entry.key === sortOrder)?.label ??
    "Recently used";

  // ESC dismissal — web only (RN Pressable does not surface keyboard events
  // at this level; native dismisses via the scrim tap wired in the View).
  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [isOpen, onClose]);

  return (
    <SeeAllPipelinesDrawerView
      isOpen={isOpen}
      loading={loading}
      error={error}
      onRetry={retry}
      personal={personal}
      team={team}
      system={system}
      isPersonalCollapsed={collapsed.personal}
      isTeamCollapsed={collapsed.team}
      isSystemCollapsed={collapsed.system}
      isEmpty={isEmpty}
      hasUnreachableBoards={hasUnreachableBoards}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      sortLabel={sortLabel}
      sortOptions={sortOptions}
      isSortOpen={isSortOpen}
      onSortToggle={handleSortToggle}
      onSortClose={handleSortClose}
      onCreatePipeline={onCreatePipeline ? handleCreate : undefined}
      onTogglePersonal={handleTogglePersonal}
      onToggleTeam={handleToggleTeam}
      onToggleSystem={handleToggleSystem}
      onSelectPipeline={handleSelect}
      onClose={onClose}
    />
  );
};

SeeAllPipelinesDrawerContainer.displayName = "SeeAllPipelinesDrawerContainer";

export default SeeAllPipelinesDrawerContainer;
